import { useState, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { OverviewCarouselProps } from './OverviewCarousel.types';
import { CAROUSEL_TRANSITION_DURATION, CAROUSEL_EASING } from './OverviewCarousel.definitions';

export function OverviewCarousel({ slides, activeSlideId, onSlideChange, ariaLabel = 'Slides' }: OverviewCarouselProps) {
	const activeIndex = Math.max(0, slides.findIndex(s => s.id === activeSlideId));
	const [isTransitioning, setIsTransitioning] = useState(false);

	const handleSlideChange = (id: string) => {
		if (id === activeSlideId) return;
		onSlideChange(id);
		setIsTransitioning(true);
	};

	const canGoLeft = activeIndex > 0;
	const canGoRight = activeIndex < slides.length - 1;

	const handlePrevious = () => {
		if (canGoLeft) {
			handleSlideChange(slides[activeIndex - 1].id);
		}
	};

	const handleNext = () => {
		if (canGoRight) {
			handleSlideChange(slides[activeIndex + 1].id);
		}
	};

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between" role="tablist" aria-label={ariaLabel} data-tour="overview-screens">
				<div className="flex gap-2">
					{slides.map(slide => (
						<button
							key={slide.id}
							id={slide.id}
							onClick={() => handleSlideChange(slide.id)}
							role="tab"
							aria-selected={slide.id === activeSlideId}
							aria-controls={`panel-${slide.id}`}
							className={clsx(
								'px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-none',
								slide.id === activeSlideId
									? 'bg-primary-500 text-white'
									: 'bg-raro-gray-100 text-raro-gray-700 hover:bg-raro-gray-200'
							)}
						>
							{slide.label}
						</button>
					))}
				</div>
			</div>

			<div className="relative overflow-hidden rounded-lg border border-raro-gray-200 bg-white">
				<div
					className="flex transition-transform motion-reduce:transition-none"
					style={{
						transform: `translateX(-${activeIndex * 100}%)`,
						transitionDuration: isTransitioning ? `${CAROUSEL_TRANSITION_DURATION}ms` : 'initial',
						transitionTimingFunction: isTransitioning ? CAROUSEL_EASING : 'initial',
					}}
					onTransitionEnd={(e) => {
						if (e.target === e.currentTarget) {
							setIsTransitioning(false);
						}
					}}
				>
					{slides.map(slide => (
						<div
							key={slide.id}
							id={`panel-${slide.id}`}
							className={clsx(
								'w-full shrink-0 transition-all motion-reduce:transition-none overflow-hidden',
								slide.id !== activeSlideId && !isTransitioning && 'h-0'
							)}
							role="tabpanel"
							aria-labelledby={slide.id}
							aria-hidden={slide.id !== activeSlideId}
							inert={slide.id !== activeSlideId ? true : undefined}
						>
							{slide.content}
						</div>
					))}
				</div>

				{canGoLeft && (
					<button
						onClick={handlePrevious}
						aria-label={`Ver ${slides[activeIndex - 1].label}`}
						className="absolute left-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/90 p-2 shadow-md hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
					>
						<ChevronLeftIcon className="h-5 w-5 text-raro-gray-900" />
					</button>
				)}

				{canGoRight && (
					<button
						onClick={handleNext}
						aria-label={`Ver ${slides[activeIndex + 1].label}`}
						className="absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/90 p-2 shadow-md hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
					>
						<ChevronRightIcon className="h-5 w-5 text-raro-gray-900" />
					</button>
				)}
			</div>
		</div>
	);
}
